import { transformResource } from '../project/utils';
import type { Workspace } from '@/types/workspace';

// ── Raw DynamoDB types ──────────────────────────────────────────

interface RawWorkspace {
  id: { S: string };
  details: {
    title: { S: string };
    creator?: { S: string };
    parent?: { S: string };
    level?: { N: string };
    image?: { S: string };
    created: { S: string };
    itemtype: { S: string };
    status?: { N: string };
    shake?: { BOOL: boolean };
  };
  expired?: { S: string };
  role: { S: string };
}

interface RawWorkspaceUser {
  user?: { S: string };
  active?: { N: string };
  role?: { S: string };
  cognitoAttributes?: { UserAttributes?: Array<{ Name: string; Value: string }> };
}

interface RawInvitedUser {
  email: { S: string };
  created: { S: string };
  expires: { S: string };
  id: { S: string };
  invitedFrom: { S: string };
  inviter: { S: string };
  project?: { S: string };
  role: { S: string };
  status: { S: string };
  group?: { S: string };
}

interface RawUsage {
  sentBytes?: { lastMonth: { N: string }; thisMonth: { N: string } };
  usedBytes: { N: string };
}

interface RawFolderItem {
  id: { S: string };
  created?: { S: string };
  endTime?: { S: string };
  group?: { S: string };
  image?: { S: string };
  index?: { N: string };
  itemtype: { S: string };
  owner?: { S: string };
  parent?: { S: string };
  parentType?: { S: string };
  path?: { S: string };
  previewImages?: unknown;
  resources?: unknown;
  searchField?: { S: string };
  startTime?: { S: string };
  status?: { N: string };
  title?: { S: string };
  name?: { S: string };
  breadcrumb?: unknown;
  [k: string]: unknown;
}

// ── Transforms ──────────────────────────────────────────────────

export function transformWorkspaces(items: RawWorkspace[]): Workspace[] {
  return items.map((w) => ({
    id: w.id.S,
    creator: w.details.creator?.S,
    title: w.details.title.S,
    status: w.details.status?.N,
    parent: w.details.parent?.S ?? 'root',
    level: Number(w.details.level?.N ?? 0),
    image: w.details?.image?.S,
    created: w.details.created.S,
    itemType: w.details.itemtype.S,
    expired: w.expired?.S,
    shakeAccess: !!w.details?.shake?.BOOL,
    role: w.role.S,
  }));
}

export function transformWorspaceUsers(items: RawWorkspaceUser[]) {
  return items.map((u) => {
    const userAttributes = getUserAttributes(u.cognitoAttributes?.UserAttributes);
    return {
      uid: u.user?.S,
      active: u.active?.N,
      role: u.role?.S,
      ...userAttributes,
    };
  });
}

export function transformInvitedUsers(items: RawInvitedUser[]) {
  return items.map((u) => ({
    email: u.email.S,
    created: u.created.S,
    expires: u.expires.S,
    id: u.id.S,
    invitedFrom: u.invitedFrom.S,
    inviter: u.inviter.S,
    project: u.project?.S,
    role: u.role.S,
    status: u.status.S,
    group: u.group?.S,
  }));
}

export function transformUsage(usage: RawUsage) {
  return {
    sentBytes: {
      lastMonth: usage?.sentBytes?.lastMonth.N,
      thisMonth: usage?.sentBytes?.thisMonth.N,
    },
    usedBytes: usage?.usedBytes.N,
  };
}

export function getUserAttributes(user?: Array<{ Name: string; Value: string }>): Record<string, string> {
  if (!user) return {};
  const userAttributes: Record<string, string> = {};
  for (const attr of user) {
    userAttributes[attr.Name] = attr.Value;
  }
  return userAttributes;
}

export function transformFolder(item: RawFolderItem) {
  return {
    id: item.id.S,
    created: item.created?.S,
    endTime: item.endTime?.S,
    group: item.group?.S,
    image: item.image?.S,
    index: item.index?.N,
    itemtype: item.itemtype?.S,
    owner: item.owner?.S,
    parent: item.parent?.S,
    parentType: item.parentType?.S,
    path: item.path?.S,
    previewImages: item.previewImages ?? [],
    resources: item.resources ?? [],
    searchField: item.searchField?.S,
    startTime: item.startTime?.S,
    status: item.status?.N,
    title: item.title?.S || item.name?.S,
    breadcrumb: item.breadcrumb,
  };
}

export function transformFolderContent(items: RawFolderItem[]) {
  return items.map((i) => {
    switch (i.itemtype.S) {
      case 'project':
        return transformProject(i);
      case 'folder':
        return transformFolder(i);
      case 'resource':
        return transformResource(i);
      default:
        return transformFolder(i);
    }
  });
}

export function transformProject(item: RawFolderItem) {
  return {
    id: item.id.S,
    projectId: item.id.S,
    created: item.created?.S,
    endTime: item.endTime?.S,
    group: item.group?.S,
    image: item.image?.S,
    index: item.index?.N,
    itemtype: item.itemtype?.S,
    owner: item.owner?.S,
    parent: item.parent?.S,
    parentType: item.parentType?.S,
    path: item.path?.S,
    previewImages: item.previewImages ?? [],
    resources: item.resources ?? [],
    searchField: item.searchField?.S,
    startTime: item.startTime?.S,
    status: item.status?.N,
    title: item.title?.S || item.name?.S,
    breadcrumb: item.breadcrumb,
  };
}

export function getCurrentRootGroup(allWorkspaces: Workspace[], currentUserGroup: string | null): Workspace | undefined {
  const current = allWorkspaces.find((i) => i.id === currentUserGroup);
  if (current?.parent === 'root') return current;
  return allWorkspaces.find((i) => i.id === current?.parent);
}
